<template>
  <div :class="$style.base">
    <CreateDiscountCode
      v-if="showCreateModal"
      @discountCode="setDiscountCode"
      @close="showCreateModal = false"
    />
    <div :class="$style.flex">
      <BaseText bold>
        {{ $t('marketing.email.sections.discount_code') }}
      </BaseText>
      <BaseToggle
        v-model="hasDiscount"
        v-test="'toggleDiscount'"
      />
    </div>
    <Flex
      direction="column"
      v-show="hasDiscount"
    >
      <BaseDropdown
        v-model="selectedDiscount"
        :label="$t('global.actions.select_code')"
        :options="
          discountCodes.map((d) => ({
            value: d.id,
            label: d.code
          }))
        "
        :required="hasDiscount"
        v-test="'select-discount-code'"
      />
      <BaseText
        @click="showCreateModal = true"
        v-test="'createDiscountCode'"
      >
        + {{ $t('marketing.discounts.new_discount_code') }}
      </BaseText>
      <Flex
        direction="column"
        :gap="0.25"
      >
        <BaseInput
          v-model="discountMessage"
          type="textarea"
          :label="$t('global.message')"
          :required="hasDiscount"
          :maxLength="150"
          v-test="'discountMessage'"
        />
        <BaseText
          color="secondary"
          size="s"
        >
          {{ $t('marketing.email.sections.discount_max_characters') }}
        </BaseText>
      </Flex>
    </Flex>
  </div>
</template>

<script lang="ts">
import useVuelidate from '@vuelidate/core';
import gql from 'graphql-tag';
import { storeToRefs } from 'pinia';
import { defineComponent } from 'vue';
import CreateDiscountCode from '@/modules/marketing/modals/create-discount-code/index.vue';
import { useMarketingStore } from '../../store';

export default defineComponent({
  components: {
    CreateDiscountCode,
  },
  setup() {
    const {
      hasDiscount,
      discount,
      discountCodeId,
      discountMessage,
      discountIsInvalid,
      isSubmitted,
      id,
    } = storeToRefs(useMarketingStore());
    const { setDiscountCode, setDiscountMessage } = useMarketingStore();
    return {
      v$: useVuelidate(),
      hasDiscount,
      discount,
      discountCodeId,
      discountMessage,
      setDiscountCode,
      setDiscountMessage,
      discountIsInvalid,
      isSubmitted,
      id,
    };
  },
  data() {
    return {
      discountCodes: [],
      showCreateModal: false,
    };
  },
  watch: {
    'v$.$invalid': {
      handler() {
        this.discountIsInvalid = this.v$.$invalid;
      },
      immediate: true,
    },
    isSubmitted() {
      this.v$.$touch();
    },
  },
  computed: {
    selectedDiscount: {
      get() {
        return this.discount?.id;
      },
      set(value) {
        const discount = this.discountCodes.find((code) => code.id === value);
        this.setDiscountCode(discount);
      },
    },
  },
  created() {
    if (this.hasDiscount && this.discount?.code) {
      this.setDiscountCode({ id: this.discount.id, code: this.discount.code });
    }
  },
  apollo: {
    discountCodes: {
      query: gql`
        query getDiscountCodes(
          $pagination: PaginationAttributes
          $state: [DiscountCodeStateEnum!]!
        ) {
          discountCodes(pagination: $pagination, state: $state) {
            id
            code
          }
        }
      `,
      variables() {
        return {
          pagination: {
            currentPage: 1,
            perPage: 1000,
          },
          state: 'ENABLED',
        };
      },
      result() {
        if (!this.id && this.discountCodes?.length) {
          this.hasDiscount = true;
        }
      },
      fetchPolicy: 'cache-and-network',
      nextFetchPolicy: 'cache-first',
    },
  },
});
</script>

<style
  lang="scss"
  module
>
.base {
  border-top: 1px solid var(--color-border);
  padding: $spacing 0;
}

.flex {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
</style>
